import { useCallback, useMemo } from "react";
import {
  Badge,
  Box,
  Icon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  WidgetListRow,
} from "@salestrak/ui";

import { AgeLabel, useClockRunning } from "#app/components/age/age-label";
import { AutomationTouchMarker } from "#app/components/automations/automation-touch-marker";
import { EntityGroupTag, EntityTag } from "#app/components/entity";
import { AssigneeSelector } from "#app/components/entity-selectors";
import { isClosedStageType, type IssueRow } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { entityFromAssigneeRef } from "#app/lib/entity-visuals";
import { useMinuteKey } from "#app/lib/hooks/use-date-boundary-key";
import { formatDueLine, nextUpdateDue } from "#app/lib/project-updates";
import {
  PROJECT_ROW_FIELD_SPEC,
  useRelatedEntityFields,
  type ProjectRowFields,
} from "#app/widgets/summary/related-entity-widget";

/**
 * The assignee cluster at the end of a project row, and the picker behind it.
 *
 * The trigger is the avatar stack itself, not a labelled control: at row scale
 * a bordered "Select assignee..." button reads as chrome the row does not have.
 * An unassigned project shows a hover-revealed add glyph instead of an empty
 * hole. `hoverReveal` already pins whatever it wraps while a popup under it is
 * open, so picking a member does not make the trigger vanish under the cursor.
 */
function ProjectRowAssignees({
  issue,
  onAssigneesChange,
}: {
  issue: IssueRow;
  onAssigneesChange: (issueId: string, entityIds: string[]) => void;
}) {
  const assignees = useMemo(
    () => issue.assignees ?? [],
    [issue.assignees],
  );
  const handleValueChange = useCallback(
    (ids: string[]) => onAssigneesChange(issue.issueId, ids),
    [issue.issueId, onAssigneesChange],
  );

  return (
    <AssigneeSelector
      onValueChange={handleValueChange}
      selectedRefs={assignees}
      value={assignees.map((ref) => ref.entityId)}
      renderTrigger={
        <Box
          align="center"
          aria-label="Assignees"
          as="button"
          direction="row"
          // The row press opens the Project; this one opens the picker.
          onClick={(event) => event.stopPropagation()}
        >
          {assignees.length > 0 ? (
            <EntityGroupTag
              entities={assignees.map(entityFromAssigneeRef)}
              interactive={false}
              size="xs"
              title="Assignees"
            />
          ) : (
            <Box hoverReveal="row">
              <Icon color="muted" name="user-plus" size="md" />
            </Box>
          )}
        </Box>
      }
    />
  );
}

/**
 * The follow-up age at the end of a project row — the one qualifier every
 * project list keeps, whatever else it drops. A closed project with no running
 * clock is finished, not stale, so it wears a check instead of a chip that
 * would read as work waiting on someone.
 *
 * `showOff` is the queue shape's opt-in: where the chip LEADS the row, an open
 * Project with no effective cycle wears the muted "No Follow up" pulse rather
 * than leaving the leading slot empty. A due band never holds such a row —
 * the server parks the stored status the moment a clock stops, and the
 * bands group on that column — so the ghost belongs to the No Follow up band,
 * where it names why no deadline follows the row.
 */
export function ProjectRowAge({
  issue,
  onAgeClick,
  showOff = false,
}: {
  issue: IssueRow;
  onAgeClick: (issue: IssueRow) => void;
  showOff?: boolean;
}) {
  const { stageMap } = useWorkspaceData();
  const running = useClockRunning(issue.age, issue.stageId);
  // No clock: a closed Stage reads as done, anything else renders nothing —
  // never an empty press that would open a posting dialog with nothing to
  // post onto. Under `showOff`, AgeLabel stands its own pressless ghost.
  if (!running) {
    if (isClosedStageType(stageMap.get(issue.stageId)?.type)) {
      return <Icon color="green" name="check" size="sm" />;
    }
    return showOff ? <AgeLabel age={issue.age} size="xs" stageId={issue.stageId} showOff /> : null;
  }
  // AgeLabel's own Button is the press; the chip is the fact.
  return (
    <AgeLabel age={issue.age} size="xs" stageId={issue.stageId} onClick={() => onAgeClick(issue)} />
  );
}

/**
 * The Follow up deadline at the end of a queue row: "19 days overdue" for a
 * row past its window, "Due in 3 days" for one inside the final stretch — the
 * same words the Follow up hover card's Next line uses, with the status named
 * so the bare "in 3 days" has a subject. Nothing renders where nothing is
 * scheduled (updates off, or no cycle resolves): the leading chip is already
 * absent there, and an empty rail says the same. Re-renders each minute so a
 * "19 minutes overdue" keeps counting while the card sits open.
 */
function ProjectRowNextUpdate({ issue }: { issue: IssueRow }) {
  const { stageMap, workflowMap } = useWorkspaceData();
  void useMinuteKey();
  const stage = stageMap.get(issue.stageId);
  const workflow = stage?.workflowId ? workflowMap.get(stage.workflowId) : undefined;
  const deadline = nextUpdateDue(issue.age, stage, workflow);
  if (!deadline) return null;
  return (
    <Text size="xs" variant="muted">
      {formatDueLine(deadline)}
    </Text>
  );
}

/**
 * One Project as a list row: stage glyph, name, then the trailing rail —
 * assignees and the follow-up age. The row every project list shares: an
 * Account's or Contact's linked Projects on the summary stack, and the
 * list-layout project cards on a dashboard (`IssueList`). The stage glyph
 * carries its workflow in a tooltip rather than a second chip, because a list
 * of projects is read for "where is each one", not "which workflow is each in".
 *
 * `ageLeads` is the QUEUE shape — for a list ordered by the Follow up, where
 * the age is the fact each row is there for: the follow-up chip takes the
 * leading slot (in place of the stage glyph), the name follows it, and the
 * trailing rail holds the deadline instead of assignees. `fields` does not
 * apply; the shape is fixed by the list's purpose. Stage and assignees wait
 * behind the press, and on a personal board the assignee is the reader.
 *
 * A project buffered on an unsaved Account has no server row yet and wears a
 * "New" badge instead of live state; its row does not open.
 *
 * Rows are `WidgetListRow`s, which owns the inset and the hover wash. Never pad
 * these rows from here.
 */
export function ProjectListRow({
  projectId,
  name,
  workflowId,
  issue,
  pending = false,
  onOpen,
  onAgeClick,
  onAssigneesChange,
  fields: fixedFields,
  ageLeads = false,
}: {
  projectId: string;
  name: string;
  workflowId: string;
  /** The live row, absent while it loads or when the project is buffered. */
  issue?: IssueRow;
  /** Buffered on an unsaved Account — nothing to open, nothing live to show. */
  pending?: boolean;
  onOpen: () => void;
  onAgeClick: (issue: IssueRow) => void;
  onAssigneesChange: (issueId: string, entityIds: string[]) => void;
  /** Fixed qualifiers in place of the reader's persisted related-entity prefs
   *  — for a list whose purpose decides what a row shows. Omitted, the row
   *  follows the shared prefs. Ignored under `ageLeads`. */
  fields?: ProjectRowFields;
  /** The queue shape: follow-up chip leading, deadline trailing. */
  ageLeads?: boolean;
}) {
  const preferredFields = useRelatedEntityFields(PROJECT_ROW_FIELD_SPEC);
  const fields = fixedFields ?? preferredFields;
  const { stageMap, workflowMap } = useWorkspaceData();
  const stage = issue ? stageMap.get(issue.stageId) : undefined;

  if (ageLeads) {
    return (
      <WidgetListRow onClick={pending ? undefined : onOpen}>
        <Box align="center" direction="row" gap="sm" minWidth={0} shrink>
          {issue ? <ProjectRowAge issue={issue} onAgeClick={onAgeClick} showOff /> : null}
          <EntityTag
            display="label"
            entity={{ id: projectId, issue: { name }, kind: "issue", workflowId }}
            interactive={false}
            weight="regular"
          />
          {pending ? (
            <Badge color="blue" size="sm">
              New
            </Badge>
          ) : null}
          <AutomationTouchMarker issueId={projectId} issueName={name} />
        </Box>
        <Box align="center" direction="row" gap="sm" justify="end" shrink={false}>
          {issue && !pending ? <ProjectRowNextUpdate issue={issue} /> : null}
        </Box>
      </WidgetListRow>
    );
  }

  return (
    <WidgetListRow onClick={pending ? undefined : onOpen}>
      <Box align="center" direction="row" gap="sm" minWidth={0} shrink>
        {fields.stage && issue && stage ? (
          <Tooltip>
            <TooltipTrigger render={<Box align="center" direction="row" />}>
              <EntityTag
                display="visual"
                entity={{ id: issue.stageId, kind: "stage", stage }}
                ghost={false}
                interactive={false}
                padded={false}
                size="sm"
              />
            </TooltipTrigger>
            <TooltipPopup side="top">
              {`${workflowMap.get(workflowId)?.name ?? "—"} · ${stage.name}`}
            </TooltipPopup>
          </Tooltip>
        ) : null}
        <EntityTag
          display="label"
          entity={{ id: projectId, issue: { name }, kind: "issue", workflowId }}
          interactive={false}
          weight="regular"
        />
        {pending ? (
          <Badge color="blue" size="sm">
            New
          </Badge>
        ) : null}
        <AutomationTouchMarker issueId={projectId} issueName={name} />
      </Box>
      <Box align="center" direction="row" gap="sm" justify="end" shrink={false}>
        {fields.assignees && issue && !pending ? (
          <ProjectRowAssignees
            issue={issue}
            onAssigneesChange={onAssigneesChange}
          />
        ) : null}
        {fields.age && issue ? <ProjectRowAge issue={issue} onAgeClick={onAgeClick} /> : null}
      </Box>
    </WidgetListRow>
  );
}
